import { Video } from "lucide-react";

import type { AgendaEvent } from "@/api/db/agenda";
import { cn } from "@/lib/utils";
import { AgendaLabels } from "@/lib/labels/agenda";

import { AGENDA_EVENT_MODAL_ID } from "./agenda-event-modal";
import { AgendaGrid, type HourGrid } from "./agenda-grid";
import { AgendaView } from "./agenda-view";

const HOUR_HEIGHT_PX = 48;
const GRID_COLUMNS = "3.5rem repeat(7, minmax(0, 1fr))";
const COMPACT_MIN = 45;

function EventBlock({
	event,
	grid,
	lane,
	selectedHash,
	onSelect,
}: {
	event: AgendaEvent;
	grid: HourGrid;
	lane: { lane: number; lanes: number };
	selectedHash: string | undefined;
	onSelect: (event: AgendaEvent) => void;
}) {
	const position = AgendaGrid.blockPosition(event, grid);
	const cancelled = AgendaView.isCancelled(event.status);
	const title = AgendaView.eventTitle(event);
	const compact = position.heightMin < COMPACT_MIN;
	const expanded = event.hash === selectedHash;

	return (
		<button
			type="button"
			onClick={() => onSelect(event)}
			aria-haspopup="dialog"
			aria-expanded={expanded}
			aria-controls={expanded ? AGENDA_EVENT_MODAL_ID : undefined}
			className={cn(
				"absolute overflow-hidden rounded-md border-l-2 px-1.5 py-0.5 text-left shadow-control transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				AgendaLabels.tipoBlock[event.tipo],
				event.tipo === "Cirurgia" && "font-medium shadow-control",
				cancelled && "opacity-40",
			)}
			style={{
				top: (position.topMin / 60) * HOUR_HEIGHT_PX + 1,
				height: (position.heightMin / 60) * HOUR_HEIGHT_PX - 2,
				left: `calc(${(lane.lane / lane.lanes) * 100}% + 2px)`,
				width: `calc(${100 / lane.lanes}% - 4px)`,
			}}
		>
			{compact && (
				<span className="flex items-center gap-1 text-xs leading-tight">
					<span className="shrink-0 font-mono text-xs opacity-80">{event.start_time}</span>
					<span className={cn("min-w-0 truncate font-medium", cancelled && "line-through")}>
						{title}
					</span>
					{event.telemedicina && <Video className="size-3 shrink-0" />}
				</span>
			)}

			{!compact && (
				<>
					<span className="flex items-center gap-1 font-mono text-xs leading-tight opacity-80">
						{event.start_time}-{event.end_time}
						{event.telemedicina && <Video className="size-3 shrink-0" />}
					</span>
					<span className={cn("block truncate text-xs leading-tight", cancelled && "line-through")}>
						{title}
					</span>
				</>
			)}
		</button>
	);
}

function DayColumn({
	date,
	events,
	grid,
	today,
	selectedHash,
	onSelect,
}: {
	date: string;
	events: AgendaEvent[];
	grid: HourGrid;
	today: string;
	selectedHash: string | undefined;
	onSelect: (event: AgendaEvent) => void;
}) {
	const lanes = AgendaGrid.lanes(events);

	return (
		<div
			className={cn("relative border-l border-border", date === today && "bg-primary/[0.04]")}
			style={{
				height: (grid.endHour - grid.startHour) * HOUR_HEIGHT_PX,
				backgroundImage: `repeating-linear-gradient(to bottom, transparent 0 ${HOUR_HEIGHT_PX - 1}px, var(--color-border) ${HOUR_HEIGHT_PX - 1}px ${HOUR_HEIGHT_PX}px)`,
			}}
		>
			{events.map((event, index) => (
				<EventBlock
					key={event.hash}
					event={event}
					grid={grid}
					lane={lanes[index]}
					selectedHash={selectedHash}
					onSelect={onSelect}
				/>
			))}
		</div>
	);
}

export function AgendaWeekGrid({
	days,
	eventsByDate,
	today,
	selectedHash,
	onSelect,
}: {
	days: string[];
	eventsByDate: Map<string, AgendaEvent[]>;
	today: string;
	selectedHash: string | undefined;
	onSelect: (event: AgendaEvent) => void;
}) {
	const grid = AgendaGrid.hourGrid(days.flatMap((date) => eventsByDate.get(date) ?? []));
	const hours = Array.from(
		{ length: grid.endHour - grid.startHour },
		(_, index) => grid.startHour + index,
	);

	return (
		<div className="workspace-frame overflow-x-auto">
			<div className="min-w-160">
				<div className="grid" style={{ gridTemplateColumns: GRID_COLUMNS }}>
					<div className="sticky left-0 z-10 border-b border-border bg-card" />
					{days.map((date) => (
						<div
							key={date}
							className={cn(
								"flex items-baseline justify-center gap-1.5 border-b border-l border-border bg-card px-1 py-2",
								date === today && "bg-primary/[0.06]",
							)}
						>
							<span
								className={cn(
									"text-xs font-medium",
									date === today ? "text-primary" : "text-muted-foreground",
								)}
							>
								{AgendaLabels.weekday(date)}
							</span>
							<span
								className={cn(
									"font-mono text-xs",
									date === today ? "font-semibold text-primary" : "text-foreground",
								)}
							>
								{date.slice(8, 10)}/{date.slice(5, 7)}
							</span>
							{date === today && (
								<span className="size-1.5 self-center rounded-full bg-destructive" />
							)}
						</div>
					))}
				</div>

				<div className="grid" style={{ gridTemplateColumns: GRID_COLUMNS }}>
					<div className="relative" style={{ height: hours.length * HOUR_HEIGHT_PX }}>
						{hours.map((hour, index) => (
							<span
								key={hour}
								className="absolute right-1.5 font-mono text-xs text-muted-foreground"
								style={{ top: index * HOUR_HEIGHT_PX + 2 }}
							>
								{hour}h
							</span>
						))}
					</div>
					{days.map((date) => (
						<DayColumn
							key={date}
							date={date}
							events={eventsByDate.get(date) ?? []}
							grid={grid}
							today={today}
							selectedHash={selectedHash}
							onSelect={onSelect}
						/>
					))}
				</div>
			</div>
		</div>
	);
}
